import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, Store, ofActionErrored } from '@ngxs/store';
import { GetCurrentUserDetails, UserRoles, UserState } from '@troyai/auth/data-access';
import { Environment } from '@troyai/envs';
import { LayoutService, Layouts } from '@troyai/layout';
import { NotificationsState } from '@troyai/notifications/data-access';
import { PharmacyContextState } from '@troyai/pharmacy-context/data-access';
import { RouterHistoryService } from '@troyai/portal/common/navigation';
import { isDemoMode } from '@troyai/portal/investor-demo/core';
import { PwaService } from '@troyai/pwa';
import { TrackService } from '@troyai/tracking';
import { TenantService } from 'multi-tenancy';
import { combineLatest, delay, filter, map } from 'rxjs';

interface Gtag {
  (command: 'config' | 'event', targetId: string, config?: unknown): void;
}

declare const gtag: Gtag;

@Component({
  selector: 't-root',
  templateUrl: './app.component.html',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class AppComponent {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private readonly router = inject(Router);
  routerHistoryService = inject(RouterHistoryService);
  private trackService = inject(TrackService);
  layoutsService = inject(LayoutService);
  private breakpointObserver = inject(BreakpointObserver);
  private tenantService = inject(TenantService);
  private titleService = inject(Title);
  private env = inject(Environment);
  private pwaService = inject(PwaService);

  constructor() {
    this.actions$.pipe(ofActionErrored(GetCurrentUserDetails)).subscribe(() => {
      this.router.navigateByUrl('/forbidden');
    });

    this.breakpointObserver
      .observe(['(min-width: 1024px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        this.mobileMode = !result.matches;
      });

    // Identify user and tenant
    combineLatest([this.selectedPharmacy$, this.userDetails$])
      .pipe(
        filter(([pharmacy, userDetails]) => !!pharmacy.global_id && !!userDetails?.global_id),
        takeUntilDestroyed()
      )
      .subscribe(([pharmacy, userDetails]) => {
        const identityProps: Record<string, string> = {
          user_id: String(userDetails?.global_id),
          tenant: String(this.tenantService.getCurrentTenantRef()),
        };

        // Add pharmacy details only if user has pharmacy access
        const hasPharmacyAccess = userDetails?.roles?.includes(UserRoles.PharmacyUser) ?? false;
        if (hasPharmacyAccess) {
          identityProps['pharmacy_name'] = pharmacy.name;
          identityProps['pharmacy_id'] = String(pharmacy.global_id);
        }
        this.trackService.identify(identityProps);
      });

    // Track page views
    this.router.events.pipe(delay(100)).subscribe((navigationEvent) => {
      if (navigationEvent instanceof NavigationEnd) {
        this.trackService.trackEvent({
          eventName: '$pageview',
          eventProps: {
            $current_url: new URL(
              navigationEvent.urlAfterRedirects,
              `https://${this.env.apiHost}`
            ).toString(),
            title: this.titleService.getTitle(),
          },
        });
        if (gtag) {
          gtag('event', 'page_view', {
            page_path: navigationEvent.urlAfterRedirects,
            page_title: this.titleService.getTitle(),
          });
        }
      }
    });
  }

  readonly Layouts = Layouts;
  /** Investor demo: the hidden controls are mounted only while demo mode is on. */
  readonly demoOn = isDemoMode();
  isAuthLoading$ = this.store.select(UserState.isLoading);
  userDetails$ = this.store.select(UserState.userDetails);
  selectedPharmacy$ = this.store.select(PharmacyContextState.selectedPharmacy);
  isAuthenticated$ = this.store.select(UserState.isLoggedIn);
  hasRoles$ = this.store.select(UserState.hasRoles);
  hasAccess$ = combineLatest([this.isAuthenticated$, this.hasRoles$]).pipe(
    map(([isAuthenticated, hasRoles]) => isAuthenticated && hasRoles)
  );
  isMOCCompliant$ = this.store.select(UserState.isMOCCompliant);
  tenantLogoUrl$ = this.store.select(UserState.currentTenantLogo);

  notificationsCount$ = this.store.select(NotificationsState.notificationsCount);
  readonly userRoles = UserRoles;

  sidebarVisible = false;
  mobileMode: boolean | null = null;

  toggleSidebar() {
    this.sidebarVisible = !this.sidebarVisible;
  }
}
